import React, { useContext, useEffect } from "react";
import FilterPopup from "../FilterPopup";
import SelectableList from "../components/SelectableList";
import {
  getSortOption as getCompanySortOption,
  DefaultCompanySortFilter,
  CompanySortOptions
} from "./FilterTypes/CompanySortFilterType";
import {
  getSortOption as getContactSortOption,
  DefaultContactSortFilter,
  ContactSortOptions
} from "./FilterTypes/ContactSortFilterType";
import {
  DefaultPaymentSortFilter,
  getSortOption as getPaymentSortOption,
  PaymentSortOptions
} from "./FilterTypes/PaymentSortFilterType";
import {
  DefaultProductionSortFilter,
  getSortOption as getProductionOption,
  ProductionSortOptions
} from "./FilterTypes/ProductionSortFilterType";
import {
  DefaultInvoiceSortFilter,
  getSortOption as getInvoiceSortOption,
  InvoiceSortOptions
} from "./FilterTypes/InvoiceSortFilterType";
import {
  getSortOption as getShowSortOption,
  DefaultShowSortFilter,
  ShowSortOptions
} from "./FilterTypes/ShowSortFilterType";
import {
  getSortOption as getPlannerSortOption,
  DefaultPlannerSortFilter,
  PlannerSortOptions
} from "./FilterTypes/PlannerSortFilterType";
import {
  DefaultDocumentSortFilter,
  getDocumentSortOptionFunc,
  getDocumentSortOptions
} from "./FilterTypes/DocumentSortFilterType";
import { FilterContext } from "../FilterContext";
import {
  getSortOption as getTaskManagerSortOption,
  DefaultTaskManagerSortFilter,
  TaskManagerSortOptions
} from "./FilterTypes/TaskSortFilterType";
import {
  getSortOption as getClientFileSortOption,
  ClientFileSortOptions,
  DefaultClientFileSortFilter
} from "./FilterTypes/ClientFileSortFilterType";
import {
  getSortOption as getClientSortOption,
  ClientSortOptions,
  DefaultClientSortFilter
} from "./FilterTypes/ClientSortFilterType";

export interface SortFilterOption<T> {
  value: T
  label: string
  enabled: boolean
}

export interface SortFilterType<T> {
  sortOption: SortFilterOption<T>
}

interface SortDtos {
  clientSort: server.dto.ClientSort
  clientFileSort: server.dto.ClientFileSort
  companySort: server.dto.CompanySort
  contactSort: server.dto.ContactSort
  paymentSort: server.dto.PaymentSort
  plannerSort: server.dto.PlannerSort
  productionSort: server.dto.ProductionSort
  invoiceSort: server.dto.InvoiceSort
  documentSort: server.dto.DocumentSort
  showSort: server.dto.ShowSort
  taskManagerSort: server.dto.TaskManagerSort
};

// TODO: Typing doesn't work right for this filter
interface SortFilterProps<K extends keyof SortDtos> {
  name: K
  getSortOption: (value: SortDtos[K]) => SortFilterOption<SortDtos[K]>
  defaultValue: SortDtos[K]
  options: Array<SortFilterOption<SortDtos[K]>>
}

const SortFilter = <K extends keyof SortDtos>(props: SortFilterProps<K>) => {
  const title = RESX.SelectionManager.SortBy;

  const { register, getValue, setInitialValue, setValue } = useContext(FilterContext);
  const reset = (): any => ({
    sortOption: props.getSortOption(props.defaultValue)
  });
  register(props.name, {
    getTitle: () => title,
    renderSelection: (value) => <>{value.sortOption.label}</>,
    hasValue: (value) => value.sortOption.value !== props.defaultValue,
    reset,
    areEqual: (a, b) => a.sortOption.value === b.sortOption.value
  });
  useEffect(() => {
    setInitialValue(props.name, reset());
  }, []);

  const [popupOpen, setPopupOpen] = React.useState<boolean>(false);
  const [selectedSortOption, setSelectedSortOption] = React.useState<SortFilterOption<SortDtos[K]>>(props.getSortOption(props.defaultValue));

  const onChange = (option: SortFilterOption<SortDtos[K]>) => {
    if (selectedSortOption?.value === option.value) {
      setValue(props.name, reset());
      setSelectedSortOption(undefined);
    } else {
      setSelectedSortOption(option);
      setValue(props.name, {
        sortOption: option
      } as any);
    }
    onPopupClose();
  };

  const onPopupClose = () => {
    setPopupOpen(false);
  };

  const onPopupOpen = () => {
    setSelectedSortOption(getValue(props.name).sortOption as SortFilterOption<SortDtos[K]>);
    setPopupOpen(true);
  };

  return <div>
    <FilterPopup
      filterButtonLabel={title}
      width={272}
      content={<div>
        <SelectableList
          noSearch
          items={props.options.filter(s => s.enabled)}
          selectedItems={selectedSortOption ? [selectedSortOption] : []}
          onItemToggled={onChange}
          getId={opt => opt.value}
          getName={opt => opt.label}
        />
      </div>}

      open={popupOpen}
      onClose={onPopupClose}
      onOpen={onPopupOpen}
    />
  </div>;
};

export const CompanySortFilter = () =>
  <SortFilter
    name="companySort"
    getSortOption={getCompanySortOption}
    defaultValue={DefaultCompanySortFilter}
    options={CompanySortOptions}
  />;

export const ContactSortFilter = () =>
  <SortFilter
    name="contactSort"
    getSortOption={getContactSortOption}
    defaultValue={DefaultContactSortFilter}
    options={ContactSortOptions}
  />;

export const PaymentSortFilter = () =>
  <SortFilter
    name="paymentSort"
    getSortOption={getPaymentSortOption}
    defaultValue={DefaultPaymentSortFilter}
    options={PaymentSortOptions}
  />;

export const InvoiceSortFilter = () =>
  <SortFilter
    name="invoiceSort"
    getSortOption={getInvoiceSortOption}
    defaultValue={DefaultInvoiceSortFilter}
    options={InvoiceSortOptions}
  />;

interface DocumentSortFilterProps {
  documentType: server.dto.DocumentTypes
}

export const DocumentSortFilter = (props: DocumentSortFilterProps) =>
  <SortFilter
    name="documentSort"
    getSortOption={getDocumentSortOptionFunc(props.documentType)}
    defaultValue={DefaultDocumentSortFilter}
    options={getDocumentSortOptions(props.documentType)}
  />;

export const ShowSortFilter = () =>
  <SortFilter
    name="showSort"
    getSortOption={getShowSortOption}
    defaultValue={DefaultShowSortFilter}
    options={ShowSortOptions()}
  />;

export const ProductionSortFilter = () =>
  <SortFilter
    name="productionSort"
    getSortOption={getProductionOption}
    defaultValue={DefaultProductionSortFilter}
    options={ProductionSortOptions}
  />;

export const PlannerSortFilter = () =>
  <SortFilter
    name="plannerSort"
    getSortOption={getPlannerSortOption}
    defaultValue={DefaultPlannerSortFilter}
    options={PlannerSortOptions}
  />;

export const TaskManagerSortFilter = () =>
  <SortFilter
    name="taskManagerSort"
    getSortOption={getTaskManagerSortOption}
    defaultValue={DefaultTaskManagerSortFilter}
    options={TaskManagerSortOptions}
  />

export const ClientFileSortFilter = () =>
  <SortFilter
    name="clientFileSort"
    getSortOption={getClientFileSortOption}
    defaultValue={DefaultClientFileSortFilter}
    options={ClientFileSortOptions}
  />

export const ClientSortFilter = () =>
  <SortFilter
    name="clientSort"
    getSortOption={getClientSortOption}
    defaultValue={DefaultClientSortFilter}
    options={ClientSortOptions}
  />